import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const PerformanceTracer = () => {
  const [performances, setPerformances] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [showAddPerformance, setShowAddPerformance] = useState(false);
  const [showAssignPerformers, setShowAssignPerformers] = useState(false);
  const [activeRole, setActiveRole] = useState('male_mover');
  const [assignedPerformers, setAssignedPerformers] = useState({
    male_movers: [],
    female_movers: [],
    instrumentalists: [],
  });
  const [newPerformance, setNewPerformance] = useState({
    name: '', location: '', date: '',
    call_time: '06:00', call_time_announced: false,
  });
  const [searchMember, setSearchMember] = useState('');

  useEffect(() => {
    fetchPerformances();
    fetchMembers();
  }, []);

  const showMsg = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchPerformances = async () => {
    try {
      setLoading(true);
      const res = await API.get('/performances');
      setPerformances(res.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const fetchMembers = async () => {
    try {
      const res = await API.get('/members');
      setMembers(res.data.filter(m => m.status === 'approved'));
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddPerformance = async () => {
    if (!newPerformance.name || !newPerformance.location || !newPerformance.date) {
      showMsg('Please fill in all required fields!', 'error');
      return;
    }
    try {
      await API.post('/performances', newPerformance);
      showMsg('✅ Performance added!', 'success');
      setShowAddPerformance(false);
      setNewPerformance({
        name: '', location: '', date: '',
        call_time: '06:00', call_time_announced: false,
      });
      fetchPerformances();
    } catch (error) {
      showMsg('Error adding performance!', 'error');
    }
  };

  const handleOpenAssign = (performance) => {
    setSelectedPerformance(performance);
    // Pre-load existing performers
    const male = performance.performers?.filter(p => p.role === 'male_mover') || [];
    const female = performance.performers?.filter(p => p.role === 'female_mover') || [];
    const inst = performance.performers?.filter(p => p.role === 'instrumentalist') || [];
    setAssignedPerformers({
      male_movers: male,
      female_movers: female,
      instrumentalists: inst,
    });
    setShowAssignPerformers(true);
  };

  const handleTogglePerformer = (member) => {
    const roleKey = activeRole === 'male_mover' ? 'male_movers' :
      activeRole === 'female_mover' ? 'female_movers' : 'instrumentalists';

    const exists = assignedPerformers[roleKey].find(p => p.id === member.id);
    if (exists) {
      setAssignedPerformers({
        ...assignedPerformers,
        [roleKey]: assignedPerformers[roleKey].filter(p => p.id !== member.id)
      });
    } else {
      setAssignedPerformers({
        ...assignedPerformers,
        [roleKey]: [...assignedPerformers[roleKey], member]
      });
    }
  };

  const handleSavePerformers = async () => {
    try {
      await API.put(`/performances/${selectedPerformance.id}/performers`, {
        male_movers: assignedPerformers.male_movers,
        female_movers: assignedPerformers.female_movers,
        instrumentalists: assignedPerformers.instrumentalists,
      });
      showMsg('✅ Performers saved!', 'success');
      setShowAssignPerformers(false);
      setSelectedPerformance(null);
      fetchPerformances();
    } catch (error) {
      showMsg('Error saving performers!', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this performance?')) return;
    try {
      await API.delete(`/performances/${id}`);
      showMsg('✅ Performance deleted!', 'success');
      fetchPerformances();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const filteredMembers = members.filter(m =>
    `${m.first_name} ${m.last_name}`.toLowerCase()
      .includes(searchMember.toLowerCase()) ||
    m.school_id.includes(searchMember)
  );

  const upcoming = performances.filter(
    p => new Date(p.date) >= new Date()
  );
  const past = performances.filter(
    p => new Date(p.date) < new Date()
  );

  const currentRoleKey = activeRole === 'male_mover' ? 'male_movers' :
    activeRole === 'female_mover' ? 'female_movers' : 'instrumentalists';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>🎭 Performance Tracer</h2>
          <p style={styles.subtitle}>
            Track all performances and assigned performers
          </p>
        </div>
        <button style={styles.addBtn}
          onClick={() => setShowAddPerformance(true)}>
          + Add Performance
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ?
          styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Summary Cards */}
      <div style={styles.cardsRow}>
        <div style={{ ...styles.card, borderTop: '4px solid #3498db' }}>
          <p style={styles.cardIcon}>🎭</p>
          <p style={styles.cardValue}>{performances.length}</p>
          <p style={styles.cardLabel}>Total Performances</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #2ecc71' }}>
          <p style={styles.cardIcon}>📅</p>
          <p style={styles.cardValue}>{upcoming.length}</p>
          <p style={styles.cardLabel}>Upcoming</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #95a5a6' }}>
          <p style={styles.cardIcon}>✅</p>
          <p style={styles.cardValue}>{past.length}</p>
          <p style={styles.cardLabel}>Completed</p>
        </div>
      </div>

      {/* Upcoming Performances */}
      {upcoming.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📅 Upcoming Performances</h3>
          <div style={styles.perfList}>
            {upcoming.map(p => (
              <PerformanceCard
                key={p.id}
                performance={p}
                onAssign={() => handleOpenAssign(p)}
                onDelete={() => handleDelete(p.id)}
                isUpcoming={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Performances */}
      {past.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>✅ Past Performances</h3>
          <div style={styles.perfList}>
            {past.map(p => (
              <PerformanceCard
                key={p.id}
                performance={p}
                onAssign={() => handleOpenAssign(p)}
                onDelete={() => handleDelete(p.id)}
                isUpcoming={false}
              />
            ))}
          </div>
        </div>
      )}

      {performances.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <p style={styles.emptyIcon}>🎭</p>
          <p style={styles.emptyText}>No performances yet!</p>
          <p style={styles.emptySubText}>
            Click "Add Performance" to get started.
          </p>
        </div>
      )}

      {/* ADD PERFORMANCE MODAL */}
      {showAddPerformance && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🎭 Add Performance</h3>
            <div style={styles.field}>
              <label style={styles.label}>Event Name *</label>
              <input style={styles.input} type="text"
                placeholder="ex. Kadayawan Festival 2024"
                value={newPerformance.name}
                onChange={e => setNewPerformance({
                  ...newPerformance, name: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Location *</label>
              <input style={styles.input} type="text"
                placeholder="ex. CSU Main Campus"
                value={newPerformance.location}
                onChange={e => setNewPerformance({
                  ...newPerformance, location: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Date *</label>
              <input style={styles.input} type="date"
                value={newPerformance.date}
                onChange={e => setNewPerformance({
                  ...newPerformance, date: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Call Time</label>
              <div style={styles.callTimeRow}>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  type="time"
                  value={newPerformance.call_time}
                  disabled={newPerformance.call_time_announced}
                  onChange={e => setNewPerformance({
                    ...newPerformance, call_time: e.target.value
                  })} />
                <label style={styles.announceLabel}>
                  <input type="checkbox"
                    checked={newPerformance.call_time_announced}
                    onChange={e => setNewPerformance({
                      ...newPerformance,
                      call_time_announced: e.target.checked
                    })} />
                  <span style={{ marginLeft: '6px' }}>
                    To be announced
                  </span>
                </label>
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAddPerformance(false)}>
                Cancel
              </button>
              <button style={styles.doneBtn}
                onClick={handleAddPerformance}>
                Add Performance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGN PERFORMERS MODAL */}
      {showAssignPerformers && selectedPerformance && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '600px' }}>
            <h3 style={styles.modalTitle}>
              👥 Assign Performers
            </h3>
            <p style={styles.modalSub}>
              🎭 {selectedPerformance.name} —{' '}
              {new Date(selectedPerformance.date)
                .toLocaleDateString('en-PH')}
            </p>

            {/* Role Tabs */}
            <div style={styles.roleTabs}>
              {[
                { id: 'male_mover', label: '🕺 Male Movers', key: 'male_movers' },
                { id: 'female_mover', label: '💃 Female Movers', key: 'female_movers' },
                { id: 'instrumentalist', label: '🎵 Instrumentalists', key: 'instrumentalists' },
              ].map(role => (
                <button
                  key={role.id}
                  style={activeRole === role.id ?
                    styles.roleTabActive : styles.roleTab}
                  onClick={() => setActiveRole(role.id)}
                >
                  {role.label}
                  <span style={styles.roleCount}>
                    {assignedPerformers[role.key].length}
                  </span>
                </button>
              ))}
            </div>

            {/* Search */}
            <input style={{ ...styles.input, marginBottom: '12px' }}
              type="text"
              placeholder="Search member..."
              value={searchMember}
              onChange={e => setSearchMember(e.target.value)} />

            {/* Member List */}
            <div style={styles.assignList}>
              {filteredMembers
                .filter(m => {
                  if (activeRole === 'male_mover') return m.gender === 'Male';
                  if (activeRole === 'female_mover') return m.gender === 'Female';
                  return true;
                })
                .map(m => {
                  const isSelected = assignedPerformers[currentRoleKey]
                    .find(p => p.id === m.id);
                  return (
                    <button
                      key={m.id}
                      style={{
                        ...styles.assignMemberBtn,
                        background: isSelected ? '#e8f0fe' : 'white',
                        border: isSelected ?
                          '2px solid #0f3460' : '1.5px solid #ddd',
                      }}
                      onClick={() => handleTogglePerformer(m)}
                    >
                      <div style={{
                        ...styles.assignAvatar,
                        background: isSelected ? '#0f3460' : '#aaa'
                      }}>
                        {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
                      </div>
                      <div style={styles.assignInfo}>
                        <p style={styles.assignName}>
                          {m.first_name} {m.last_name}
                        </p>
                        <p style={styles.assignId}>{m.school_id}</p>
                      </div>
                      {isSelected && (
                        <span style={styles.checkMark}>✓</span>
                      )}
                    </button>
                  );
                })}
            </div>

            {/* Summary */}
            <div style={styles.assignSummary}>
              <p style={styles.summaryText}>
                🕺 Male: {assignedPerformers.male_movers.length} ·
                💃 Female: {assignedPerformers.female_movers.length} ·
                🎵 Inst: {assignedPerformers.instrumentalists.length}
              </p>
            </div>

            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => {
                  setShowAssignPerformers(false);
                  setSelectedPerformance(null);
                }}>
                Cancel
              </button>
              <button style={styles.doneBtn}
                onClick={handleSavePerformers}>
                Save Performers
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Performance Card Component
const PerformanceCard = ({ performance, onAssign, onDelete, isUpcoming }) => {
  const [showDetails, setShowDetails] = useState(false);
  const male = performance.performers?.filter(p => p.role === 'male_mover') || [];
  const female = performance.performers?.filter(p => p.role === 'female_mover') || [];
  const inst = performance.performers?.filter(p => p.role === 'instrumentalist') || [];

  return (
    <div style={cardStyles.card}>
      <div style={cardStyles.cardTop}>
        <div style={cardStyles.dateBox}>
          <span style={cardStyles.month}>
            {new Date(performance.date).toLocaleDateString('en-PH', { month: 'short' })}
          </span>
          <span style={cardStyles.day}>
            {new Date(performance.date).getDate()}
          </span>
          <span style={cardStyles.year}>
            {new Date(performance.date).getFullYear()}
          </span>
        </div>
        <div style={cardStyles.info}>
          <h4 style={cardStyles.name}>{performance.name}</h4>
          <p style={cardStyles.detail}>📍 {performance.location}</p>
          <p style={cardStyles.detail}>
            ⏰ {performance.call_time_announced ?
              'To be announced' : performance.call_time?.slice(0, 5)}
          </p>
          <div style={cardStyles.performerCount}>
            <span style={cardStyles.countBadge}>
              🕺 {male.length}
            </span>
            <span style={cardStyles.countBadge}>
              💃 {female.length}
            </span>
            <span style={cardStyles.countBadge}>
              🎵 {inst.length}
            </span>
          </div>
        </div>
        <div style={cardStyles.actions}>
          <button style={cardStyles.assignBtn} onClick={onAssign}>
            👥 Assign
          </button>
          <button style={cardStyles.detailBtn}
            onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? '▲' : '▼'}
          </button>
          <button style={cardStyles.deleteBtn} onClick={onDelete}>
            🗑️
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {showDetails && (
        <div style={cardStyles.details}>
          <div style={cardStyles.performerGroups}>
            <div style={cardStyles.group}>
              <p style={cardStyles.groupTitle}>🕺 Male Movers</p>
              {male.length === 0 ?
                <p style={cardStyles.noOne}>Not assigned</p> :
                male.map(p => (
                  <p key={p.id} style={cardStyles.performer}>
                    {p.first_name} {p.last_name}
                  </p>
                ))
              }
            </div>
            <div style={cardStyles.group}>
              <p style={cardStyles.groupTitle}>💃 Female Movers</p>
              {female.length === 0 ?
                <p style={cardStyles.noOne}>Not assigned</p> :
                female.map(p => (
                  <p key={p.id} style={cardStyles.performer}>
                    {p.first_name} {p.last_name}
                  </p>
                ))
              }
            </div>
            <div style={cardStyles.group}>
              <p style={cardStyles.groupTitle}>🎵 Instrumentalists</p>
              {inst.length === 0 ?
                <p style={cardStyles.noOne}>Not assigned</p> :
                inst.map(p => (
                  <p key={p.id} style={cardStyles.performer}>
                    {p.first_name} {p.last_name}
                  </p>
                ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '20px',
    flexWrap: 'wrap', gap: '10px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  addBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  cardsRow: {
    display: 'flex', gap: '15px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  card: {
    flex: '1', minWidth: '140px', background: 'white',
    borderRadius: '12px', padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  cardIcon: { fontSize: '28px', margin: '0 0 8px 0' },
  cardValue: {
    fontSize: '24px', fontWeight: 'bold',
    color: '#333', margin: '0 0 5px 0',
  },
  cardLabel: { fontSize: '12px', color: '#888', margin: 0 },
  section: { marginBottom: '25px' },
  sectionTitle: {
    margin: '0 0 15px 0', fontSize: '16px',
    fontWeight: 'bold', color: '#333',
  },
  perfList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  emptyState: { textAlign: 'center', padding: '50px 0' },
  emptyIcon: { fontSize: '50px', margin: '0 0 10px 0' },
  emptyText: { color: '#555', fontSize: '16px', margin: '0 0 5px 0' },
  emptySubText: { color: '#aaa', fontSize: '13px', margin: 0 },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1001,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '16px',
    padding: '30px', width: '100%', maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: {
    margin: '0 0 8px 0', fontSize: '18px',
    fontWeight: 'bold', color: '#333',
  },
  modalSub: {
    margin: '0 0 20px 0', color: '#888',
    fontSize: '13px', background: '#f8f9fa',
    padding: '8px 12px', borderRadius: '8px',
  },
  field: { marginBottom: '15px' },
  label: {
    display: 'block', fontSize: '13px',
    fontWeight: '600', color: '#444', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px',
    borderRadius: '10px', border: '1.5px solid #ddd',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  callTimeRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  announceLabel: {
    display: 'flex', alignItems: 'center',
    fontSize: '13px', color: '#555',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelBtn: {
    flex: 1, padding: '12px', background: 'white',
    color: '#666', border: '1.5px solid #ddd',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '14px',
  },
  doneBtn: {
    flex: 1, padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
  roleTabs: {
    display: 'flex', gap: '8px',
    marginBottom: '15px', flexWrap: 'wrap',
  },
  roleTab: {
    flex: 1, padding: '8px 12px', background: 'white',
    border: '1.5px solid #ddd', borderRadius: '10px',
    cursor: 'pointer', fontSize: '12px', color: '#666',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '6px',
  },
  roleTabActive: {
    flex: 1, padding: '8px 12px', background: '#0f3460',
    border: '1.5px solid #0f3460', borderRadius: '10px',
    cursor: 'pointer', fontSize: '12px', color: 'white',
    fontWeight: 'bold', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
  roleCount: {
    background: 'rgba(255,255,255,0.3)',
    borderRadius: '10px', padding: '1px 6px',
    fontSize: '11px',
  },
  assignList: {
    maxHeight: '250px', overflowY: 'auto',
    marginBottom: '12px',
  },
  assignMemberBtn: {
    width: '100%', padding: '10px',
    borderRadius: '8px', cursor: 'pointer',
    display: 'flex', alignItems: 'center',
    gap: '10px', marginBottom: '6px', textAlign: 'left',
  },
  assignAvatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    color: 'white', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontWeight: 'bold', fontSize: '13px', flexShrink: 0,
  },
  assignInfo: { flex: 1 },
  assignName: {
    margin: '0 0 2px 0', fontWeight: 'bold',
    color: '#333', fontSize: '13px',
  },
  assignId: { margin: 0, color: '#888', fontSize: '11px' },
  checkMark: {
    color: '#0f3460', fontWeight: 'bold', fontSize: '18px',
  },
  assignSummary: {
    background: '#f8f9fa', borderRadius: '8px',
    padding: '10px', marginBottom: '5px',
  },
  summaryText: {
    margin: 0, fontSize: '13px',
    color: '#555', textAlign: 'center',
  },
};

const cardStyles = {
  card: {
    background: 'white', borderRadius: '14px',
    padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  cardTop: {
    display: 'flex', gap: '15px',
    alignItems: 'flex-start', flexWrap: 'wrap',
  },
  dateBox: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', background: '#0f3460',
    borderRadius: '10px', padding: '10px 15px',
    minWidth: '60px', flexShrink: 0,
  },
  month: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '11px', textTransform: 'uppercase',
  },
  day: {
    color: 'white', fontSize: '24px',
    fontWeight: 'bold', lineHeight: '1',
  },
  year: { color: 'rgba(255,255,255,0.5)', fontSize: '11px' },
  info: { flex: 1 },
  name: {
    margin: '0 0 6px 0', fontSize: '16px',
    fontWeight: 'bold', color: '#333',
  },
  detail: { margin: '0 0 4px 0', color: '#888', fontSize: '13px' },
  performerCount: {
    display: 'flex', gap: '6px', marginTop: '8px',
  },
  countBadge: {
    padding: '3px 10px', background: '#f0f2f5',
    borderRadius: '20px', fontSize: '12px', color: '#555',
  },
  actions: {
    display: 'flex', gap: '6px', alignItems: 'flex-start',
  },
  assignBtn: {
    padding: '8px 14px', background: '#0f3460',
    color: 'white', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '12px',
  },
  detailBtn: {
    padding: '8px 12px', background: '#f0f2f5',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px',
  },
  deleteBtn: {
    padding: '8px 10px', background: 'none',
    border: '1px solid #ddd', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px',
  },
  details: {
    marginTop: '15px', paddingTop: '15px',
    borderTop: '1px solid #f0f0f0',
  },
  performerGroups: {
    display: 'flex', gap: '15px', flexWrap: 'wrap',
  },
  group: { flex: 1, minWidth: '120px' },
  groupTitle: {
    margin: '0 0 8px 0', fontWeight: 'bold',
    color: '#555', fontSize: '13px',
  },
  noOne: { color: '#aaa', fontSize: '12px', margin: 0 },
  performer: {
    margin: '0 0 4px 0', color: '#333', fontSize: '12px',
  },
};

export default PerformanceTracer;